import * as Sentry from '@sentry/nextjs';
import { Elements } from '@stripe/react-stripe-js';
import Button from 'components/buttons/button';
import { ButtonGrid } from 'components/buttons/payments';
import StripeError from 'components/payments/PaymentError';
import { useAnalytics } from 'hooks/useAnalytics';
import useMediaQuery from 'hooks/useMediaQuery';
import { convertDonationToCoinValue, getCoinPrices } from 'lib/api/premium';
import getStripe from 'lib/getStripe';
import { getStripeAppearanceConfig } from 'lib/stripe';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import {
  BankAccountPropType,
  CardPaymentMethodPropType,
  CashAppPropType,
  LinkPropType,
} from 'utilities/propTypes';
import { normalizePaymentMethod } from 'utilities/stripe/paymentMethod';
import { ExistingPaymentMethodDisplay } from './PaymentMethodDisplay';
import SetupForm from './SetupForm';

const stripePromise = getStripe();

const INITIAL_SELECTED_BUTTON = {
  cents: 2500,
};

const payButtonContent = ({ amountCents }) => {
  if (amountCents === 0) {
    return 'Make a Selection';
  } else {
    return `Buy ${convertDonationToCoinValue({
      donation_cents: amountCents,
    }).toLocaleString()} coins for $${amountCents / 100}`;
  }
};

async function sendRequest(url, { arg }) {
  Sentry.addBreadcrumb({
    category: 'stripe:create-one-time-payment:coins',
    message: 'Sending request to create one time payment',
    data: arg,
  });
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(arg),
  });

  // https://swr.vercel.app/docs/error-handling#status-code-and-error-object
  if (!res.ok) {
    const error = new Error('An error occurred while fetching the data.');
    // Attach extra info to the error object.
    const resData = await res.json();

    Sentry.addBreadcrumb({
      category: 'stripe:create-one-time-payment:coins',
      message: 'Error creating one time payment',
      data: resData,
    });

    error.info = resData;
    error.status = res.status;
    throw error;
  }
  return res.json();
}

const BuyCoinsForm = ({ customer, clientSecret = null, paymentMethod }) => {
  const { donationTrackEvent } = useAnalytics();
  const [selectedButton, setSelectedButton] = useState(
    INITIAL_SELECTED_BUTTON.cents
  );
  const [amountCents, setAmountCents] = useState(INITIAL_SELECTED_BUTTON.cents);
  const [canSubmit, setCanSubmit] = useState(false);
  const [validPaymentMethod, setValidPaymentMethod] = useState(paymentMethod);
  const [failedPayment, setFailedPayment] = useState(false);
  const router = useRouter();
  const coinPrices = getCoinPrices({
    isPremium: customer?.subscription?.active,
  });

  const handleButtonClick = ({ id }) => {
    const button = coinPrices.find((button) => button.id === id);
    setSelectedButton(button.id);
    setAmountCents(button.cents);
  };

  // This logic is duplicated in BuyPremium.js
  const onPaymentMethodAdded = async (payload) => {
    fetch('/api/payments/set-payment-method-default', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        stripeCustomerId: customer.id,
        paymentMethodId: payload.paymentMethodId,
        uuid: customer.uuid,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        Sentry.addBreadcrumb({
          category: 'stripe:set-payment-method-default',
          message: 'Default payment method set via api',
          data: data,
        });

        const normalizedPaymentMethod = normalizePaymentMethod({
          defaultPaymentMethod: data.payment_method,
        });
        setValidPaymentMethod(normalizedPaymentMethod);
      });
  };

  // SWR to create the one time payment
  const { trigger, isMutating } = useSWRMutation(
    '/api/payments/purchase-coins',
    sendRequest
  );

  const finalizeCoinPurchase = async () => {
    setCanSubmit(false);

    const payload = {
      email: customer.email,
      uuid: customer.uuid,
      customerId: customer.id,
      amountCents: amountCents,
      paymentMethodId: validPaymentMethod.id,
    };

    // Fetch the stripe radar session
    try {
      Sentry.addBreadcrumb({
        category: 'stripe:createRadarSession',
        message: 'Requesting the Stripe Radar Session',
        data: payload,
      });
      const stripe = await stripePromise;
      const { radarSession, error } = await stripe.createRadarSession();

      if (error) {
        Sentry.captureException(error);
      } else {
        payload.radarSessionId = radarSession.id;
      }
    } catch (e) {
      Sentry.captureException(e);
    }

    // Trigger the payment
    // Some of this logic is duplicated in BuyPremium.js
    try {
      Sentry.addBreadcrumb({
        category: 'stripe:create-one-time-payment:coins',
        message: 'Sending request to create one time payment',
        data: payload,
      });
      const result = await trigger(payload);
      Sentry.addBreadcrumb({
        category: 'stripe:create-one-time-payment:coins',
        message: 'Received response from create one time payment',
        data: result,
      });
      donationTrackEvent({
        donationValueCents: amountCents,
      });
      // Redirect to the thanks page
      router.push({
        pathname: '/premium/thanks',
        query: { pi: result.paymentIntent.id },
      });
    } catch (e) {
      const eventId = Sentry.captureException(e);
      setFailedPayment({ error: e.info?.error || e, eventId });
    }
  };

  // Checks to see if the form is ready to be submitted
  useEffect(() => {
    if (amountCents > 0 && validPaymentMethod) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [amountCents, validPaymentMethod]);

  // Get stripe form apprearance config
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const appearanceConfig = getStripeAppearanceConfig({ darkMode: prefersDark });

  return failedPayment ? (
    <StripeError
      error={failedPayment.error}
      sentryEventId={failedPayment.eventId}
    />
  ) : (
    <>
      <ButtonGrid
        data={coinPrices}
        selectedButtonId={selectedButton}
        handleButtonClick={handleButtonClick}
        isSubscriber={customer?.subscription?.active}
      />

      <div className="mt-2">
        {!validPaymentMethod
          ? clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance: appearanceConfig }}
              >
                <SetupForm onComplete={onPaymentMethodAdded} />
              </Elements>
            )
          : ExistingPaymentMethodDisplay({
              paymentMethod: validPaymentMethod,
              billingPortalSession: customer.billingPortalSession,
            })}
      </div>

      <div className="mt-2">
        <Button
          theme="blue"
          onClick={finalizeCoinPurchase}
          disabled={!canSubmit}
          preventDefault
        >
          {payButtonContent({ amountCents })}
        </Button>
        {isMutating && <p>Away we go!! ✈️</p>}
      </div>
    </>
  );
};

BuyCoinsForm.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
  }).isRequired,
  paymentMethod: PropTypes.oneOfType([
    CardPaymentMethodPropType,
    BankAccountPropType,
    LinkPropType,
    CashAppPropType,
  ]),
  clientSecret: PropTypes.string,
};

export default BuyCoinsForm;
